'use client';

import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLanguage } from '@/components/language-provider';
import { Download, CheckCircle2, XCircle, Quote } from 'lucide-react';

const DEG = '°C';

const abrasionRef: [string, string][] = [
  ['NBR', '0.1–0.2'],
  ['Q (silicone)', '0.5–0.8'],
  ['PU', '0.03–0.1'],
  ['NR', '0.2–0.3'],
];

const abrasionMeasured: [string, string][] = [
  ['Rubber 50#', '0.29'],
  ['Rubber 60#', '0.12'],
  ['ZL-PAD-70A', '0.03'],
  ['TPU sample', '0.05'],
];

const wetSlip: [string, string, string, string, string][] = [
  ['1', 'TPU sample', '0.258', '0.09', ''],
  ['2', 'ZL-PAD-70A', '0.536 / 0.7', '0.12', '☆☆☆'],
  ['3', 'BH-M-NR70', '0.137', '0.11', ''],
];

const perf40a: [string, string, string, string][] = [
  ['zlpad.prop.color', '/', 'BLACK', 'VISUAL'],
  ['zlpad.prop.material', '/', 'MODRUBBER', '/'],
  ['zlpad.prop.density', 'g/cm³', '1.2 ± 0.1', 'GB/T 533'],
  ['zlpad.prop.hardness', 'Shore A', '40 ± 5', 'GB/T 39693.4'],
  ['zlpad.prop.tensile', 'MPa', '8', 'GB/T 528'],
  ['zlpad.prop.elongation', '%', '500', 'GB/T 528'],
  ['zlpad.prop.compression', '%', '20', 'GB/T 7759'],
  ['zlpad.prop.tanmax', '/', '1.2', 'GB/T 9870.1'],
  ['zlpad.prop.tan06', DEG, '−25 to 36', 'GB/T 9870.1'],
  ['zlpad.prop.tan04', DEG, '−50 to 48', 'GB/T 9870.1'],
  ['zlpad.prop.tan03', DEG, '−50 to 55', 'GB/T 9870.1'],
  ['zlpad.prop.temprange', DEG, '−45 to 140', '/'],
];

const perf70a: [string, string, string, string][] = [
  ['zlpad.prop.color', '/', 'BLACK', 'VISUAL'],
  ['zlpad.prop.material', '/', 'MODRUBBER', '/'],
  ['zlpad.prop.density', 'g/cm³', '1.2 ± 0.1', 'GB/T 533'],
  ['zlpad.prop.hardness', 'Shore A', '70 ± 5', 'GB/T 39693.4'],
  ['zlpad.prop.tensilemin', 'MPa', '10', 'GB/T 528'],
  ['zlpad.prop.elongationmin', '%', '300', 'GB/T 528'],
  ['zlpad.prop.compression', '%', '15', 'GB/T 7759'],
  ['zlpad.prop.tanmax', '/', '0.72', 'GB/T 9870.1'],
  ['zlpad.prop.tan0', '/', '0.70', 'GB/T 9870.1'],
  ['zlpad.prop.tan04', DEG, '−35 to 32', 'GB/T 9870.1'],
  ['zlpad.prop.tan03', DEG, '−50 to 48', 'GB/T 9870.1'],
  ['zlpad.prop.temprange', DEG, '−45 to 140', '/'],
];

const materials = ['nbr', 'q', 'pu', 'nr'] as const;

export default function RobotDogPadPage() {
  const { t } = useLanguage();

  const resolveProp = (key: string): string => {
    if (key === 'BLACK') return t('zlpad.val.black');
    if (key === 'VISUAL') return t('zlpad.val.visual');
    if (key === 'MODRUBBER') return t('zlpad.val.modrubber');
    if (key === 'zlpad.prop.tensilemin') return `${t('zlpad.prop.tensile')} (min)`;
    if (key === 'zlpad.prop.elongationmin') return `${t('zlpad.prop.elongation')} (min)`;
    return t(key);
  };

  const renderPerfTable = (titleKey: string, rows: [string, string, string, string][]) => (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t(titleKey)}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('zlpad.s6.col.property')}</TableHead>
              <TableHead>{t('zlpad.s6.col.unit')}</TableHead>
              <TableHead>{t('zlpad.s6.col.value')}</TableHead>
              <TableHead>{t('zlpad.s6.col.standard')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(([prop, unit, value, std], i) => (
              <TableRow key={i}>
                <TableCell className="font-medium">{resolveProp(prop)}</TableCell>
                <TableCell>{unit}</TableCell>
                <TableCell className="font-semibold">{value}</TableCell>
                <TableCell className="text-muted-foreground">{std}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-4">{t('zlpad.hero.badge')}</Badge>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
              {t('zlpad.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              {t('zlpad.hero.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              <Badge className="text-sm px-4 py-1.5">{t('zlpad.feature.cushioning')}</Badge>
              <Badge className="text-sm px-4 py-1.5">{t('zlpad.feature.antislip')}</Badge>
              <Badge className="text-sm px-4 py-1.5">{t('zlpad.feature.wear')}</Badge>
              <Badge className="text-sm px-4 py-1.5">{t('zlpad.feature.quiet')}</Badge>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              '/zlpad/robot-dog-unitree.jpg',
              '/zlpad/zlpad-front.jpg',
              '/zlpad/zlpad-tread.jpg',
            ].map((src) => (
              <div key={src} className="rounded-xl overflow-hidden border bg-card">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={src} alt="ZL-PAD robot dog foot pad" className="w-full h-64 object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 1: Foot Components */}
      <section className="py-16 bg-muted/40">
        <div className="container mx-auto px-4">
          <div className="max-w-sm mx-auto mb-10">
            <div className="relative w-full overflow-hidden rounded-xl border border-border shadow-lg bg-black">
              <video
                className="w-full h-auto"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                controls
              >
                <source src="/robot1.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{t('zlpad.s1.title')}</h2>
          <div className="grid md:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
            <div className="space-y-4">
              {[
                { k: 'func', icon: CheckCircle2 },
                { k: 'role', icon: CheckCircle2 },
                { k: 'problem', icon: XCircle },
              ].map(({ k, icon: Icon }) => (
                <Card key={k}>
                  <CardContent className="flex gap-3 pt-6">
                    <Icon className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
                    <div>
                      <p className="font-semibold">{t(`zlpad.s1.${k}.title`)}</p>
                      <p className="text-sm text-muted-foreground">{t(`zlpad.s1.${k}.content`)}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="rounded-xl overflow-hidden border bg-card">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/zlpad/zlpad-wear.jpg" alt={t('zlpad.s1.wearcap')} className="w-full h-72 object-cover" />
              <p className="text-sm text-muted-foreground p-4">{t('zlpad.s1.wearcap')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Materials */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{t('zlpad.s2.title')}</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {materials.map((m) => (
              <Card key={m}>
                <CardHeader>
                  <CardTitle className="text-lg">{t(`zlpad.mat.${m}.name`)}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <CheckCircle2 className="h-4 w-4 mt-1 shrink-0 text-primary" />
                    <p className="text-sm text-muted-foreground">{t(`zlpad.mat.${m}.pros`)}</p>
                  </div>
                  <div className="flex gap-2">
                    <XCircle className="h-4 w-4 mt-1 shrink-0 text-destructive" />
                    <p className="text-sm text-muted-foreground">{t(`zlpad.mat.${m}.cons`)}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Akron Abrasion */}
      <section className="py-16 bg-muted/40">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{t('zlpad.s3.title')}</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('zlpad.s3.table1.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('zlpad.s3.col.rubber')}</TableHead>
                      <TableHead>{t('zlpad.s3.col.abrasion')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {abrasionRef.map(([r, v], i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{r}</TableCell>
                        <TableCell>{v}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('zlpad.s3.table2.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('zlpad.s3.col.rubber')}</TableHead>
                      <TableHead>{t('zlpad.s3.col.abrasion')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {abrasionMeasured.map(([r, v], i) => (
                      <TableRow key={i} className={r === 'ZL-PAD-70A' ? 'bg-primary/5' : ''}>
                        <TableCell className="font-medium">{r}</TableCell>
                        <TableCell className="font-semibold">{v}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
          <div className="flex items-center justify-center gap-4 mt-8 max-w-5xl mx-auto">
            <div className="rounded-xl overflow-hidden border bg-card w-48 shrink-0">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/zlpad/akron-tester.jpg" alt="Akron abrasion tester" className="w-full h-36 object-cover" />
            </div>
            <p className="text-sm text-muted-foreground">
              <Quote className="inline h-4 w-4 mr-1" />
              {t('zlpad.s3.note')}
            </p>
          </div>
        </div>
      </section>

      {/* Section 4: Wet-Slip */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{t('zlpad.s4.title')}</h2>
          <Card className="max-w-4xl mx-auto">
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('zlpad.s4.col.no')}</TableHead>
                    <TableHead>{t('zlpad.s3.col.rubber')}</TableHead>
                    <TableHead>{t('zlpad.s4.col.tand')}</TableHead>
                    <TableHead>{t('zlpad.s4.col.temp')}</TableHead>
                    <TableHead>{t('zlpad.s4.col.remarks')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wetSlip.map(([no, r, tan, tmp, remark], i) => (
                    <TableRow key={i} className={r === 'ZL-PAD-70A' ? 'bg-primary/5' : ''}>
                      <TableCell>{no}</TableCell>
                      <TableCell className="font-medium">{r}</TableCell>
                      <TableCell>{tan}</TableCell>
                      <TableCell>{tmp}</TableCell>
                      <TableCell>{remark}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-muted-foreground mt-4">{t('zlpad.s4.note')}</p>
            </CardContent>
          </Card>
          <Card className="max-w-4xl mx-auto mt-6">
            <CardHeader>
              <CardTitle className="text-base">{t('zlpad.s4.cond.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">{t('zlpad.s4.cond.desc')}</p>
              {['freq', 'strain', 'temp'].map((k) => (
                <div key={k} className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <p className="text-sm text-muted-foreground">{t(`zlpad.s4.cond.${k}`)}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Section 5: Structure Recommendation */}
      <section className="py-16 bg-muted/40">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">{t('zlpad.s5.title')}</h2>
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            <Badge variant="secondary">{t('zlpad.s5.middle')}</Badge>
            <Badge variant="secondary">{t('zlpad.s5.bottom')}</Badge>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="rounded-xl overflow-hidden border bg-card md:col-span-1">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/zlpad/zlpad-cross-section.jpg" alt={t('zlpad.s5.desc')} className="w-full h-80 object-cover" />
              <p className="text-sm text-muted-foreground p-4">{t('zlpad.s5.desc')}</p>
            </div>
            <div className="rounded-xl overflow-hidden border bg-card">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/zlpad/zlpad-product.jpg" alt={t('zlpad.s5.product')} className="w-full h-80 object-cover" />
              <p className="text-sm text-muted-foreground p-4">{t('zlpad.s5.product')}</p>
            </div>
            <div className="rounded-xl overflow-hidden border bg-card">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/zlpad/zlpad-field-test.jpg" alt={t('zlpad.s5.fieldtest')} className="w-full h-80 object-cover" />
              <p className="text-sm text-muted-foreground p-4">{t('zlpad.s5.fieldtest')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Performance Tables */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{t('zlpad.s6.title')}</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto items-start">
            {renderPerfTable('zlpad.s6.table40a', perf40a)}
            {renderPerfTable('zlpad.s6.table70a', perf70a)}
          </div>
          <p className="text-sm text-muted-foreground text-center max-w-4xl mx-auto mt-6">{t('zlpad.s6.notes')}</p>
        </div>
      </section>

      {/* Section 7: DMA Curves */}
      <section className="py-16 bg-muted/40">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{t('zlpad.s7.title')}</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="rounded-xl overflow-hidden border bg-card">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/zlpad/dma-curve-40a.jpg" alt="ZL-PAD-40A DMA curve" className="w-full h-72 object-contain bg-card" />
              <p className="text-sm text-muted-foreground p-4 text-center">ZL-PAD-40A</p>
            </div>
            <div className="rounded-xl overflow-hidden border bg-card">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/zlpad/dma-curve-70a.jpg" alt="ZL-PAD-70A DMA curve" className="w-full h-72 object-contain bg-card" />
              <p className="text-sm text-muted-foreground p-4 text-center">ZL-PAD-70A</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl font-bold mb-4">{t('zlpad.cta.title')}</h2>
          <p className="text-lg text-muted-foreground mb-2">{t('zlpad.cta.desc')}</p>
          <p className="text-muted-foreground mb-8">{t('zlpad.cta.contact')}</p>
          <a
            href="/downloads/zl-pad-brochure.pdf"
            download="ZL-PAD_Robot_Dog_Foot_Pad_Brochure.pdf"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-10 px-8"
          >
            {t('zlpad.cta.button')}
            <Download className="ml-2 h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
